import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";

import { I18nProvider } from "@/context/I18nContext";
import { AuthProvider } from "@/context/AuthContext";

import LandingPage from "@/pages/public/LandingPage";
import BookingFlow from "@/pages/public/BookingFlow";
import CheckoutPage from "@/pages/public/CheckoutPage";
import ConfirmationPage from "@/pages/public/ConfirmationPage";
import CoordinationReceivedPage from "@/pages/public/CoordinationReceivedPage";
import ContactPage from "@/pages/public/ContactPage";
import FaqPage from "@/pages/public/FaqPage";
import TermsPage from "@/pages/public/TermsPage";
import PrivacyPage from "@/pages/public/PrivacyPage";
import SmsProgramPage from "@/pages/public/SmsProgramPage";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminBookingDetail from "@/pages/admin/AdminBookingDetail";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminZones from "@/pages/admin/AdminZones";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminUsers from "@/pages/admin/AdminUsers";
import NotFoundPage from "@/pages/public/NotFoundPage";
import AboutPage from "@/pages/public/AboutPage";
import TrackingPage from "@/pages/public/TrackingPage";
import ZoneLandingPage from "@/pages/public/ZoneLandingPage";
import ForHostsPage from "@/pages/public/ForHostsPage";
import HostConnectPage from "@/pages/public/HostConnectPage";
import AutoCancelPage from "@/pages/public/AutoCancelPage";
import AutoReschedulePage from "@/pages/public/AutoReschedulePage";
import WorkWithUsPage from "@/pages/public/WorkWithUsPage";
import CleanerApplyPage from "@/pages/public/CleanerApplyPage";
import ApplicationReceivedPage from "@/pages/public/ApplicationReceivedPage";
import CleanerLoginPage from "@/pages/cleaner/CleanerLoginPage";
import CleanerDashboardPage from "@/pages/cleaner/CleanerDashboardPage";
import CleanerBookingDetailPage from "@/pages/cleaner/CleanerBookingDetailPage";
import CleanerChangePasswordPage from "@/pages/cleaner/CleanerChangePasswordPage";
import CleanerSetupPasswordPage from "@/pages/cleaner/CleanerSetupPasswordPage";
import BackgroundCheckWizardPage from "@/pages/cleaner/BackgroundCheckWizardPage";
import CleanerEarningsPage from "@/pages/cleaner/CleanerEarningsPage";
import CleanerStripeReturnPage from "@/pages/cleaner/CleanerStripeReturnPage";
import ServiceAgreementPage from "@/pages/public/ServiceAgreementPage";
import CleanerAgreementPage from "@/pages/public/CleanerAgreementPage";
import WaitlistPage from "@/pages/public/WaitlistPage";
import AdminPromos from "@/pages/admin/AdminPromos";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminWaitlist from "@/pages/admin/AdminWaitlist";
import AdminCleanerResponses from "@/pages/admin/AdminCleanerResponses";
import AdminHosts from "@/pages/admin/AdminHosts";
import AdminCoordination from "@/pages/admin/AdminCoordination";
import AdminCleaners from "@/pages/admin/AdminCleaners";
import AdminApplications from "@/pages/admin/AdminApplications";
import AdminIdentityChecks from "@/pages/admin/AdminIdentityChecks";
import AdminQrGenerator from "@/pages/admin/AdminQrGenerator";
import AdminGallery from "@/pages/admin/AdminGallery";
import ErrorBoundary from "@/components/shared/ErrorBoundary";
import ScrollToTop from "@/components/shared/ScrollToTop";

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <I18nProvider>
          <AuthProvider>
            <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* Public */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/book" element={<BookingFlow />} />
              <Route path="/checkout/:id" element={<CheckoutPage />} />
              <Route path="/confirmation/:id" element={<ConfirmationPage />} />
              <Route path="/coordination/:id" element={<CoordinationReceivedPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/track" element={<TrackingPage />} />
              <Route path="/track/:bookingNumber" element={<TrackingPage />} />
              <Route path="/cleaning-services/:slug" element={<ZoneLandingPage />} />
              <Route path="/for-hosts" element={<ForHostsPage />} />
              <Route path="/host/connect" element={<HostConnectPage />} />
              <Route path="/auto-cancel/:token" element={<AutoCancelPage />} />
              <Route path="/auto-reschedule/:token" element={<AutoReschedulePage />} />
              <Route path="/work-with-us" element={<WorkWithUsPage />} />
              <Route path="/join-as-cleaner" element={<WorkWithUsPage />} />
              <Route path="/work-with-us/apply" element={<CleanerApplyPage />} />
              <Route path="/work-with-us/application-received" element={<ApplicationReceivedPage />} />
              <Route path="/cleaner/login" element={<CleanerLoginPage />} />
              <Route path="/cleaner/dashboard" element={<CleanerDashboardPage />} />
              <Route path="/cleaner/bookings/:id" element={<CleanerBookingDetailPage />} />
              <Route path="/cleaner/change-password" element={<CleanerChangePasswordPage />} />
              <Route path="/cleaner/setup-password" element={<CleanerSetupPasswordPage />} />
              <Route path="/cleaner/background-check" element={<BackgroundCheckWizardPage />} />
              <Route path="/cleaner/earnings" element={<CleanerEarningsPage />} />
              <Route path="/cleaner/stripe/complete" element={<CleanerStripeReturnPage />} />
              <Route path="/cleaner/stripe/refresh" element={<CleanerStripeReturnPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/sms" element={<SmsProgramPage />} />
              <Route path="/service-agreement" element={<ServiceAgreementPage />} />
              <Route path="/cleaner-agreement" element={<CleanerAgreementPage />} />
              <Route path="/waitlist" element={<WaitlistPage />} />

              {/* Admin */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="bookings" element={<AdminBookings />} />
                <Route path="bookings/:id" element={<AdminBookingDetail />} />
                <Route path="coordination" element={<AdminCoordination />} />
                <Route path="pricing" element={<AdminPricing />} />
                <Route path="promos" element={<AdminPromos />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="waitlist" element={<AdminWaitlist />} />
                <Route path="cleaner-responses" element={<AdminCleanerResponses />} />
                <Route path="hosts" element={<AdminHosts />} />
                <Route path="audit" element={<AdminAuditLog />} />
                <Route path="zones" element={<AdminZones />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="cleaners" element={<AdminCleaners />} />
                <Route path="applications" element={<AdminApplications />} />
                <Route path="identity-checks" element={<AdminIdentityChecks />} />
                <Route path="qr-generator" element={<AdminQrGenerator />} />
                <Route path="gallery" element={<AdminGallery />} />
              </Route>

              {/* 404 — keep last */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
